import { z } from 'zod';
import { idSchema } from './ids';
import { roleSchema } from './enums';
import { permissionSchema } from './permissions';

/** HTTP wiring shared by the API, the web client and the tests (ADR 0005). */
export const SESSION_COOKIE = 'pmai_sid';
export const CSRF_HEADER = 'x-csrf-token';

/** Web pages outside the module registry (auth flows). E-mail links point here. */
export const AUTH_PAGES = {
  login: '/login',
  register: '/register',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  acceptInvitation: '/invitations/accept',
} as const;
export type AuthPage = (typeof AUTH_PAGES)[keyof typeof AUTH_PAGES];

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 200;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email({ message: 'Nederīga e-pasta adrese' }).max(254));

export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, { message: `Parolei jābūt vismaz ${PASSWORD_MIN_LENGTH} simbolus garai` })
  .max(PASSWORD_MAX_LENGTH);

export const displayNameSchema = z.string().trim().min(1).max(100);
export const workspaceNameSchema = z.string().trim().min(1).max(100);

/** One-time tokens (email verification, password reset, invitation) — opaque, URL-safe. */
export const oneTimeTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{32,128}$/, 'Nederīga saite');

export const registerSchema = z.object({
  email: emailSchema,
  password: newPasswordSchema,
  displayName: displayNameSchema,
  workspaceName: workspaceNameSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const tokenBodySchema = z.object({ token: oneTimeTokenSchema });

export const passwordResetRequestSchema = z.object({ email: emailSchema });
export const passwordResetConfirmSchema = z.object({
  token: oneTimeTokenSchema,
  password: newPasswordSchema,
});

export const switchWorkspaceSchema = z.object({ workspaceId: idSchema });

export const membershipSchema = z.object({
  workspaceId: idSchema,
  workspaceName: z.string(),
  role: roleSchema,
});
export type Membership = z.infer<typeof membershipSchema>;

export const meSchema = z.object({
  user: z.object({ id: idSchema, email: z.string(), displayName: z.string() }),
  workspace: membershipSchema.nullable(),
  memberships: z.array(membershipSchema),
  permissions: z.array(permissionSchema),
  csrfToken: z.string(),
});
export type Me = z.infer<typeof meSchema>;

/** True for responses that carry the session (`Me`); clients take the CSRF token from them. */
export const carriesSession = (body: unknown): body is Me =>
  typeof body === 'object' && body !== null && 'csrfToken' in body && 'user' in body;

export const sessionInfoSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;

/** Generic acknowledgement for requests whose outcome must not reveal account existence. */
export const acceptedSchema = z.object({ accepted: z.literal(true) });
